

































































































































import Vue from "vue";
import TasksListItem from "./TasksListItem.vue";
import moment, { unitOfTime } from "moment";
import { Task, UserAssignment } from "@/tasks";
import i18n from "./../../plugins/i18n";
import { TaskSortOption } from "../../enums";
import storeHelper from "@/store/store-helper";
import { Member } from "@/teams";
import ZenyaDialog from "../dialogs/ZenyaDialog.vue";
import PostponeDialog from "./PostponeDialog.vue";

export default Vue.extend({
    name: "TasksList",
    props: {
        value: Array as () => Task[],
        team: Boolean,
        showDividers: Boolean,
        sort: String,
        notifications: Boolean,
        teamMembers: Array as () => Member[],
        bus: Object as () => Vue
    },
    components: {
        TasksListItem,
        ZenyaDialog,
        PostponeDialog
    },
    data: () => {
        return {
            selectedItemIDs: [],
            maxRowsAtStart: 25,
            maxRows: 25,
            addRowCount: 25,
            postponeUntilDatePicker: false
        };
    },
    watch: {
        value() {
            if (this.showDividers) this.setDividerText();
        },
        showDividers() {
            if (this.showDividers) this.setDividerText();
        }
    },
    created() {
        // Calculate how many tasks fit on screen
        this.maxRowsAtStart = (document.body.offsetHeight - 200) / 90;
        this.maxRows = this.maxRowsAtStart;
        this.addRowCount = this.maxRowsAtStart;

        moment.locale(i18n.locale);
        moment.updateLocale("en", {
            week: {
                dow: 1 // Monday is the first day of the week.
            }
        });
    },
    destroyed() {
        window.removeEventListener("scroll", this.onScroll);
    },
    mounted() {
        if (this.showDividers) this.setDividerText();

        //Check when set on scroll event is triggert from parent by event bus method (https://code.luasoftware.com/tutorials/vuejs/parent-call-child-component-method/)
        this.bus?.$on("set-on-scroll-event", this.setOnScrollEvent);
    },
    computed: {
        items(): Task[] {
            return this.value.slice(0, this.maxRows);
        },
        xsView(): boolean {
            return this.$vuetify.breakpoint.name === "xs";
        },
        filteredSelectedItemIDs(): string[] {
            //hack for firefox because it sometimes selects the tasklistitem index instead of the task id
            return this.selectedItemIDs.filter(a => typeof a !== "number");
        },
        isAllSelected(): boolean {
            return this.selectedItemIDs.length === this.value.length;
        }
    },
    methods: {
        setOnScrollEvent() {
            // Remove the existing scroll event
            window.removeEventListener("scroll", this.onScroll);

            // Set zazy load/render tasks
            window.addEventListener("scroll", this.onScroll);

            // Make sure we reset shown items count
            this.maxRowsAtStart = 25;
            this.maxRows = 25;
            this.addRowCount = 25;
        },
        onScroll() {
            if (window.innerHeight + window.scrollY >= document.body.offsetHeight && this.maxRows < this.value.length) {
                this.loadMoreRows();
            }
        },
        loadMoreRows() {
            this.maxRows += this.addRowCount;
            if (this.showDividers) this.setDividerText();
        },
        showDateDivider(item: Task): string {
            const itemDate = moment(item.last_modified_date_time);

            if (this.sort === TaskSortOption.Important && this.value.find(x => x.is_important) === item)
                return this.$t("tasks.list.isImportant").toString();

            if (this.sort === TaskSortOption.Created || (this.sort === TaskSortOption.Important && !item.is_important)) {
                if (this.dateIsSame(moment().toDate(), item.last_modified_date_time, "week")) {
                    //This week
                    if (this.getItemsInPeriod(itemDate, "week")[0] === item) return this.$t("general.time.thisWeek").toString();
                    else return null;
                } else if (this.dateIsSame(moment().subtract(1, "week").toDate(), item.last_modified_date_time, "week")) {
                    //Last week
                    if (this.getItemsInPeriod(itemDate, "week")[0] === item) return this.$t("general.time.lastWeek").toString();
                    else return null;
                } else if (this.dateIsSame(moment().subtract(2, "week").toDate(), item.last_modified_date_time, "week")) {
                    //Two weeks ago
                    if (this.getItemsInPeriod(itemDate, "week")[0] === item) return this.$t("general.time.twoWeeks").toString();
                    else return null;
                } else if (this.dateIsSame(moment().subtract(3, "week").toDate(), item.last_modified_date_time, "week")) {
                    //Three weeks ago
                    if (this.getItemsInPeriod(itemDate, "week")[0] === item) return this.$t("general.time.threeWeeks").toString();
                    else return null;
                } else if (this.dateIsSame(moment().subtract(1, "month").toDate(), item.last_modified_date_time, "month")) {
                    //Last month
                    const itemsLastMonth = this.value.filter(x =>
                        this.sort === TaskSortOption.Important
                            ? !x.is_important &&
                              this.dateIsSame(x.last_modified_date_time, itemDate.toDate(), "month") &&
                              itemDate.isBefore(moment().subtract(3, "week"))
                            : this.dateIsSame(x.last_modified_date_time, itemDate.toDate(), "month") &&
                              itemDate.isBefore(moment().subtract(3, "week"))
                    );
                    if (itemsLastMonth[0] === item) return this.$t("general.time.lastMonth").toString();
                    else return null;
                } else {
                    //Older
                    const itemsOlder = this.value.filter(x => moment(x.last_modified_date_time).isBefore(moment().subtract(1, "month")));
                    if (itemsOlder[0] === item) return this.$t("general.time.older").toString();
                    else return null;
                }
            }
        },
        showDeadlineDivider(item: Task): string {
            const itemDate = moment(item.deadline_date_time);
            const today = moment();

            if (itemDate.isBefore(today)) {
                //deadline passed
                const itemsOlder = this.value.filter(x => moment(x.deadline_date_time).isBefore(today));
                if (itemsOlder[0] === item) return this.$t("tasks.list.deadlineDividers.deadlinePassed").toString();
                else return null;
            } else if (this.dateIsSame(today.toDate(), item.deadline_date_time, "day")) {
                //today
                if (this.getItemsInPeriod(itemDate, "day")[0] === item) return this.$t("general.time.today").toString();
                else return null;
            } else if (this.dateIsSame(today.add(1, "day").toDate(), item.deadline_date_time, "day")) {
                //tomorrow
                if (this.getItemsInPeriod(itemDate, "day")[0] === item) return this.$t("general.time.tomorrow").toString();
                else return null;
            } else if (this.dateIsSame(moment().toDate(), item.deadline_date_time, "week")) {
                //this week
                if (this.getItemsInPeriod(itemDate, "week").filter(x => x.deadline_date_time > moment().add(1, "day").toDate())[0] == item)
                    return this.$t("general.time.thisWeek").toString();
                else return null;
            } else if (this.dateIsSame(moment().add(1, "week").toDate(), item.deadline_date_time, "week")) {
                //next week
                if (this.getItemsInPeriod(itemDate, "week")[0] === item) return this.$t("general.time.nextWeek").toString();
                else return null;
            } else if (this.dateIsSame(moment().add(2, "week").toDate(), item.deadline_date_time, "week")) {
                //in two weeks
                if (this.getItemsInPeriod(itemDate, "week")[0] === item) return this.$t("general.time.inTwoWeeks").toString();
                else return null;
            } else if (this.dateIsSame(moment().toDate(), itemDate.toDate(), "month")) {
                //this month
                const itemsThisMonth = this.value.filter(x => moment(x.deadline_date_time).isAfter(moment().add(2, "week")));
                if (itemsThisMonth[0] === item) return this.$t("general.time.thisMonth").toString();
                else return null;
            } else if (this.dateIsSame(moment().add(1, "month").toDate(), itemDate.toDate(), "month")) {
                //next month
                var itemsNextMonth;
                if (itemDate.month() > today.month()) {
                    itemsNextMonth = this.value.filter(x => moment(x.deadline_date_time).isAfter(moment().add(2, "week"), "week"));
                } else {
                    itemsNextMonth = this.value.filter(x => moment(x.deadline_date_time).isAfter(moment(), "month"));
                }
                if (itemsNextMonth[0] === item) return this.$t("general.time.nextMonth").toString();
                else return null;
            } else if (moment(itemDate).isAfter(moment().add(1, "month"), "month")) {
                //later
                const itemsOlder = this.value.filter(x => moment(x.deadline_date_time).isAfter(moment().add(1, "month"), "month"));
                if (itemsOlder[0] === item) return this.$t("general.time.later").toString();
                return null;
            }

            if (this.value.find(x => !x.deadline_date_time) === item) return this.$t("tasks.list.deadlineDividers.withoutDeadline").toString();

            return null;
        },
        setDividerText() {
            if (this.value.length) {
                for (const item of this.value.slice(0, this.maxRows)) {
                    if (this.sort === TaskSortOption.Deadline) item.divider_text = this.showDeadlineDivider(item);
                    else item.divider_text = this.showDateDivider(item);
                }
            }
        },
        dateIsSame(date1: Date, date2: Date, period: unitOfTime.StartOf): boolean {
            return moment(date1).isSame(moment(date2), period);
        },
        getItemsInPeriod(itemDate: moment.Moment, period: unitOfTime.StartOf): Task[] {
            const items = this.value;
            if (this.sort === TaskSortOption.Deadline) {
                return items.filter(x => this.dateIsSame(x.deadline_date_time, itemDate.toDate(), period));
            } else if (this.sort === TaskSortOption.Important) {
                return items.filter(x => !x.is_important && this.dateIsSame(x.last_modified_date_time, itemDate.toDate(), period));
            } else {
                return items.filter(x => this.dateIsSame(x.last_modified_date_time, itemDate.toDate(), period));
            }
        },
        onAssignedUserChanged(task: Task, user: UserAssignment) {
            task.assigned_to_user = user;
        },
        taskSelectionChanged(taskId: string, selected: boolean): void {
            if (selected) {
                if (!this.selectedItemIDs.find(x => x === taskId)) {
                    this.selectedItemIDs.push(taskId);
                }
            } else {
                this.selectedItemIDs = this.selectedItemIDs.filter(x => x !== taskId);
            }
        },
        async deleteSelectedNotificationsAsync(): Promise<void> {
            let task: Task;
            let id: string;
            if (this.notifications) {
                while (this.filteredSelectedItemIDs.length > 0) {
                    id = this.selectedItemIDs.pop();
                    task = this.value.find(x => x.id == id);

                    //sanity check, but should always be true
                    if (task) {
                        await storeHelper.notificationsStore.deleteNotification(task.id);
                    }
                }
            }
        },
        async setSelectedImportanceAsync(important: boolean): Promise<void> {
            let task: Task;
            let id: string;
            while (this.filteredSelectedItemIDs.length > 0) {
                id = this.selectedItemIDs.pop();
                task = this.value.find(x => x.id == id);

                if (task) {
                    if (this.notifications) await storeHelper.notificationsStore.updateIsImportantNotificationAsync(task.id, important);
                    else await storeHelper.tasksStore.updateIsImportantTaskAsync(task.id, important);
                }
            }
        },
        async updatePostPonedDate(newPostponedDate: Date) {
            let task: Task;
            let id: string;
            while (this.filteredSelectedItemIDs.length > 0) {
                id = this.selectedItemIDs.pop();
                task = this.value.find(x => x.id == id);

                //sanity check, but should always be true
                if (task) {
                    await storeHelper.tasksStore.updatePostponedUntilDateTaskAsync(task.id, newPostponedDate);
                }
            }
        },
        selectAllChanged() {
            if (!this.isAllSelected) {
                this.selectedItemIDs = this.value.map(x => x.id);
            } else this.selectedItemIDs = [];
        },
        getIsSelected(itemID: string) {
            return this.filteredSelectedItemIDs.indexOf(itemID) != -1;
        }
    }
});
